import { Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material';
import React from 'react';

export interface ColumnCheckItemProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * One column row in the picker.
 *
 * Uses `FormControlLabel` rather than the shared `molecules/CheckItem`, which
 * puts the name in the input's `aria-label` beside a plain `Typography` — that
 * leaves the visible text inert, so the only hit target is the 20px box. Here
 * the label is a real `<label>`: the whole row is clickable and the accessible
 * name comes from the text itself.
 */
const ColumnCheckItemComponent: React.FC<ColumnCheckItemProps> = ({ label, checked, onChange }) => {
  const theme = useTheme();

  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          sx={{ py: 0.25, color: theme.palette.v4.borderDark }}
        />
      }
      label={
        <Typography variant="body2" noWrap title={label}>
          {label}
        </Typography>
      }
      sx={{
        display: 'flex',
        width: '100%',
        minWidth: 0,
        // The label makes the whole row the hit target; the floor keeps that row
        // comfortably tappable once the dialog goes full-screen on small viewports.
        minHeight: 34,
        m: 0,
        pr: 1,
        borderRadius: 1,
        '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden' },
        '&:hover': { bgcolor: theme.palette.action.hover },
      }}
    />
  );
};

// ~120 rows render at once on the Jobs picker; memoising keeps a single tick
// from re-rendering all of them.
export const ColumnCheckItem = React.memo(ColumnCheckItemComponent);
